import { Link } from 'react-router-dom';
import Hero from './components/Hero';
import Testimonials from './components/Testimonials';
import BookingCTA from './components/BookingCTA';
import LeaveReview from './components/LeaveReview';
import SeoHead from '../../components/feature/SeoHead';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

const homeSchema = {
  '@context': 'https://schema.org',
  '@type': ['MedicalBusiness', 'LocalBusiness'],
  name: 'Lotus Maternal',
  description: 'Doula services for all families providing culturally competent support through fertility, pregnancy, birth, and postpartum.',
  url: siteUrl,
  telephone: '(929)473-8867',
  email: 'support@lotusmaternal.com',
  image: 'https://static.readdy.ai/image/0e3c418cd49273f94bb710e1b839df62/e757ad2e61eee82f71f2dc519555a206.png',
  logo: 'https://static.readdy.ai/image/0e3c418cd49273f94bb710e1b839df62/e757ad2e61eee82f71f2dc519555a206.png',
  priceRange: '$600-$5000',
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'Brooklyn',
    addressRegion: 'NY',
    postalCode: '11201',
    addressCountry: 'US',
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: 40.6782,
    longitude: -73.9442,
  },
  areaServed: [
    { '@type': 'City', name: 'Brooklyn' },
    { '@type': 'City', name: 'Manhattan' },
    { '@type': 'City', name: 'Queens' },
    { '@type': 'City', name: 'Bronx' },
    { '@type': 'City', name: 'Staten Island' },
  ],
  openingHoursSpecification: [
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'],
      opens: '00:00',
      closes: '23:59',
    },
  ],
  sameAs: ['https://instagram.com/lotus.maternal'],
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: 'Doula Service Packages',
    itemListElement: [
      { '@type': 'Offer', name: 'Princess Package', price: '600', priceCurrency: 'USD', availability: 'https://schema.org/InStock' },
      { '@type': 'Offer', name: 'Queen Package', price: '800', priceCurrency: 'USD', availability: 'https://schema.org/InStock' },
      { '@type': 'Offer', name: 'Empress Package', price: '1000', priceCurrency: 'USD', availability: 'https://schema.org/InStock' },
      { '@type': 'Offer', name: 'Goddess Package', price: '1200', priceCurrency: 'USD', availability: 'https://schema.org/InStock' },
      { '@type': 'Offer', name: 'Prenatal Package', price: '2500', priceCurrency: 'USD', availability: 'https://schema.org/InStock' },
      { '@type': 'Offer', name: 'Postpartum Package', price: '2000', priceCurrency: 'USD', availability: 'https://schema.org/InStock' },
      { '@type': 'Offer', name: 'Full Journey Package', price: '5000', priceCurrency: 'USD', availability: 'https://schema.org/InStock' },
    ],
  },
};

export default function HomePage() {
  return (
    <div className="min-h-screen">
      <SeoHead
        title="Lotus Maternal | Brooklyn Doula Services — Pregnancy, Birth & Postpartum Support"
        description="Lotus Maternal provides compassionate doula services for all families in Brooklyn, NY. Expert support through fertility, pregnancy, birth, and postpartum. Book your free 30-minute consultation today."
        keywords="doula services Brooklyn NY, pregnancy support, birth doula, postpartum care, maternal health, family doula New York"
        canonical={`${siteUrl}/`}
        ogTitle="Lotus Maternal — Doula Services for All Families"
        ogDescription="Expert doula support through fertility, pregnancy, birth, and postpartum. Culturally competent care for all families in Brooklyn, NY."
        schema={homeSchema}
      />
      {/* Hero Section */}
      <Hero />

      {/* Mission Preview Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-pink-50 via-white to-mint-50">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://public.readdy.ai/ai/img_res/7a742e799b12241a9dcff1c0ef8814ad.jpg"
                alt="Lotus Maternal Mission"
                className="w-full h-[500px] object-cover rounded-2xl shadow-xl"
              />
            </div>
            <div>
              <h2 className="font-playfair text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                Supporting Families Through Every Step
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Lotus Maternal provides doula services designed to support all families throughout their fertility, pregnancy, birth, and postpartum journey.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-8">
                Our goal is to address healthcare disparities by providing culturally competent care and support. We ensure that every family feels empowered, informed, and supported throughout their journey to parenthood.
              </p>
              <Link
                to="/about"
                className="inline-flex items-center gap-2 bg-gradient-to-r from-pink-400 to-pink-500 text-white px-8 py-4 rounded-full font-semibold hover:from-pink-500 hover:to-pink-600 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
              >
                Learn More About Us
                <i className="ri-arrow-right-line text-xl"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services Preview Strip */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-playfair text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Our Services
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Comprehensive support for every stage of your journey
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Pregnancy Service */}
            <Link to="/services" className="group cursor-pointer">
              <div className="relative h-80 rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all">
                <img
                  src="https://readdy.ai/api/search-image?query=Radiant%20African%20American%20pregnant%20woman%20in%20comfortable%20soft%20clothing%20doing%20prenatal%20yoga%20meditation%20peaceful%20serene%20expression%20soft%20natural%20lighting%20warm%20golden%20tones%20wellness%20health%20focus%20simple%20clean%20background%20rich%20deep%20melanin%20skin%20glowing&width=400&height=500&seq=service-preview-pregnancy-v3-001&orientation=portrait"
                  alt="Pregnancy Support"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                  <h3 className="font-playfair text-3xl font-bold mb-2">Pregnancy</h3>
                  <p className="text-white/90 mb-4">Comprehensive prenatal care and education</p>
                  <span className="inline-flex items-center gap-2 text-pink-300 font-semibold whitespace-nowrap">
                    Learn More <i className="ri-arrow-right-line"></i>
                  </span>
                </div>
              </div>
            </Link>

            {/* Birth Service */}
            <Link to="/services" className="group cursor-pointer">
              <div className="relative h-80 rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all">
                <img
                  src="https://readdy.ai/api/search-image?query=African%20American%20woman%20in%20labor%20with%20supportive%20Black%20doula%20beside%20her%20holding%20hands%20calm%20empowering%20birth%20environment%20warm%20soft%20lighting%20emotional%20support%20and%20care%20hospital%20birth%20center%20setting%20authentic%20maternal%20healthcare%20rich%20deep%20melanin%20skin%20tones&width=400&height=500&seq=service-preview-birth-v3-001&orientation=portrait"
                  alt="Birth Support"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                  <h3 className="font-playfair text-3xl font-bold mb-2">Birth</h3>
                  <p className="text-white/90 mb-4">Coaching and support during labor</p>
                  <span className="inline-flex items-center gap-2 text-pink-300 font-semibold whitespace-nowrap">
                    Learn More <i className="ri-arrow-right-line"></i>
                  </span>
                </div>
              </div>
            </Link>

            {/* Postpartum Service */}
            <Link to="/services" className="group cursor-pointer">
              <div className="relative h-80 rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all">
                <img
                  src="https://readdy.ai/api/search-image?query=Beautiful%20African%20American%20mother%20with%20natural%20afro%20hair%20holding%20newborn%20baby%20tender%20loving%20expression%20breastfeeding%20bonding%20moment%20soft%20warm%20natural%20light%20peaceful%20postpartum%20scene%20simple%20clean%20background%20intimate%20maternal%20care%20rich%20deep%20melanin%20skin%20tones&width=400&height=500&seq=service-preview-postpartum-v3-001&orientation=portrait"
                  alt="Postpartum Support"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                  <h3 className="font-playfair text-3xl font-bold mb-2">Postpartum</h3>
                  <p className="text-white/90 mb-4">Care and support for new parents</p>
                  <span className="inline-flex items-center gap-2 text-pink-300 font-semibold whitespace-nowrap">
                    Learn More <i className="ri-arrow-right-line"></i>
                  </span>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Packages Teaser Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-mint-50 via-pink-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-playfair text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Our Packages
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Choose the perfect package for your journey
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {/* Princess Package */}
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all border-2 border-pink-100">
              <div className="text-center">
                <h3 className="font-playfair text-2xl font-bold text-gray-900 mb-2">Princess</h3>
                <div className="text-4xl font-bold text-pink-500 mb-4">$600</div>
                <ul className="text-left space-y-2 text-gray-700 mb-6">
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-mint-500 text-xl flex-shrink-0 w-6 h-6 flex items-center justify-center"></i>
                    <span>1 Prenatal/Postpartum Visit</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-mint-500 text-xl flex-shrink-0 w-6 h-6 flex items-center justify-center"></i>
                    <span>1 OB/Midwife Visit</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-mint-500 text-xl flex-shrink-0 w-6 h-6 flex items-center justify-center"></i>
                    <span>Nutritionist Meal Plan</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Queen Package */}
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all border-2 border-pink-100">
              <div className="text-center">
                <h3 className="font-playfair text-2xl font-bold text-gray-900 mb-2">Queen</h3>
                <div className="text-4xl font-bold text-pink-500 mb-4">$800</div>
                <ul className="text-left space-y-2 text-gray-700 mb-6">
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-mint-500 text-xl flex-shrink-0 w-6 h-6 flex items-center justify-center"></i>
                    <span>2 Prenatal/Postpartum Visits</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-mint-500 text-xl flex-shrink-0 w-6 h-6 flex items-center justify-center"></i>
                    <span>2 OB/Midwife Visits</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-mint-500 text-xl flex-shrink-0 w-6 h-6 flex items-center justify-center"></i>
                    <span>1 Protective Hairstyle</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-mint-500 text-xl flex-shrink-0 w-6 h-6 flex items-center justify-center"></i>
                    <span>24/7 Call/Text Support</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Empress Package */}
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all border-2 border-pink-100">
              <div className="text-center">
                <h3 className="font-playfair text-2xl font-bold text-gray-900 mb-2">Empress</h3>
                <div className="text-4xl font-bold text-pink-500 mb-4">$1,000</div>
                <ul className="text-left space-y-2 text-gray-700 mb-6">
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-mint-500 text-xl flex-shrink-0 w-6 h-6 flex items-center justify-center"></i>
                    <span>3 Prenatal/Postpartum Visits</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-mint-500 text-xl flex-shrink-0 w-6 h-6 flex items-center justify-center"></i>
                    <span>3 OB/Midwife Visits</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-mint-500 text-xl flex-shrink-0 w-6 h-6 flex items-center justify-center"></i>
                    <span>1 Protective Hairstyle</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-mint-500 text-xl flex-shrink-0 w-6 h-6 flex items-center justify-center"></i>
                    <span>24/7 Call/Text Support</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Goddess Package */}
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all border-2 border-pink-100">
              <div className="text-center">
                <h3 className="font-playfair text-2xl font-bold text-gray-900 mb-2">Goddess</h3>
                <div className="text-4xl font-bold text-pink-500 mb-4">$1,200</div>
                <ul className="text-left space-y-2 text-gray-700 mb-6">
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-mint-500 text-xl flex-shrink-0 w-6 h-6 flex items-center justify-center"></i>
                    <span>4 Prenatal/Postpartum Visits</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-mint-500 text-xl flex-shrink-0 w-6 h-6 flex items-center justify-center"></i>
                    <span>4 OB/Midwife Visits</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-mint-500 text-xl flex-shrink-0 w-6 h-6 flex items-center justify-center"></i>
                    <span>Help With Nursery Setup</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-mint-500 text-xl flex-shrink-0 w-6 h-6 flex items-center justify-center"></i>
                    <span>Perineal Bath & More</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="text-center">
            <Link
              to="/packages"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-pink-400 to-pink-500 text-white px-10 py-4 rounded-full font-semibold hover:from-pink-500 hover:to-pink-600 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              View All Packages
              <i className="ri-arrow-right-line text-xl"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials Preview */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-playfair text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              What Families Say
            </h2>
            <p className="text-lg text-gray-600">
              Hear from the families we've supported
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Testimonial 1 */}
            <div className="bg-gradient-to-br from-pink-50 to-mint-50 rounded-2xl p-8 shadow-lg">
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-yellow-400 text-xl w-6 h-6 flex items-center justify-center"></i>
                ))}
              </div>
              <p className="text-gray-700 text-lg leading-relaxed mb-6">
                "Queen Brie was absolutely amazing throughout my entire pregnancy and birth. She made me feel empowered and supported every step of the way. I couldn't have done it without her!"
              </p>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-pink-300 rounded-full flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                  T
                </div>
                <div>
                  <div className="font-semibold text-gray-900">Tasha M.</div>
                  <div className="text-sm text-gray-600">Brooklyn, NY</div>
                </div>
              </div>
            </div>

            {/* Testimonial 2 */}
            <div className="bg-gradient-to-br from-mint-50 to-pink-50 rounded-2xl p-8 shadow-lg">
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-yellow-400 text-xl w-6 h-6 flex items-center justify-center"></i>
                ))}
              </div>
              <p className="text-gray-700 text-lg leading-relaxed mb-6">
                "The postpartum support I received was life-changing. Having someone who understood my cultural needs and provided judgment-free care made all the difference in my recovery."
              </p>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-mint-400 rounded-full flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                  K
                </div>
                <div>
                  <div className="font-semibold text-gray-900">Keisha R.</div>
                  <div className="text-sm text-gray-600">Harlem, NY</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Testimonials />
      <LeaveReview />
      <BookingCTA />
    </div>
  );
}